import { matchPath } from 'react-router-dom';
import { compile } from 'path-to-regexp';
// NOTE: This file imports urlHelpers.js, which may lead to circular dependency
import { stringify } from './urlHelpers';

const findRouteByName = (nameToFind, routes) => routes.find(route => route.name === nameToFind);

/**
 * E.g. ```const toListingPath = toPathByRouteName('ListingPage', routes);```
 * Then we can generate listing paths with given params (```toListingPath({ id: uuidX })```)
 */
const toPathByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Path "${nameToFind}" was not found.`);
  }
  return compile(route.path);
};

/**
 * Shorthand for single path call. (```pathByRouteName('ListingPage', routes, { id: uuidX });```)
 */
export const pathByRouteName = (nameToFind, routes, params = {}) => {
  const hasEmptySlug = params && params.hasOwnProperty('slug') && params.slug === '';
  const pathParams = hasEmptySlug ? { ...params, slug: 'no-slug' } : params;
  return toPathByRouteName(nameToFind, routes)(pathParams);
};

/**
 * Find the matching routes and their params for the given pathname
 *
 * @param {String} pathname - Full URL path from root with possible
 * search params and hash included
 *
 * @return {Array<{ route, params }>} - All matches as { route, params } objects if matches has
 * exact flag set to false. If not, an array containing just the first matched exact route is returned.
 */
export const matchPathname = (pathname, routeConfiguration) => {
  const matchedRoutes = routeConfiguration.reduce((matches, route) => {
    const { path, exact = true } = route;
    const match = matchPath(pathname, { path, exact });
    if (match) {
      matches.push({
        route,
        params: match.params || {},
      });
    }
    return matches;
  }, []);

  const matchedExactRoute = matchedRoutes.find(r => {
    return r.exact === true || r.exact == null;
  });

  // We return matched 'exact' path route only if such exists
  // and all matches if no exact flag exists.
  return matchedExactRoute ? [matchedExactRoute] : matchedRoutes;
};

/**
 * ResourceLocatorString is used to direct webapp to correct page.
 * In contrast to Universal Resource Locator (URL), this doesn't contain protocol, host, or port.
 */
export const createResourceLocatorString = (
  routeName,
  routes,
  pathParams = {},
  searchParams = {},
  hash = ''
) => {
  const searchQuery = stringify(searchParams);
  const includeSearchQuery = searchQuery.length > 0 ? `?${searchQuery}` : '';
  const path = pathByRouteName(routeName, routes, pathParams);
  return `${path}${includeSearchQuery}${hash}`;
};

/**
 * Find component related to route name
 * E.g. `const PageComponent = findComponentByRouteName('CheckoutPage', routes);`
 * Then we can call static methods of given component:
 * `dispatch(PageComponent.setInitialValues({ listing, bookingDates }));`
 *
 * @param {String} nameToFind - Route name
 * @param {Array<{ route }>} routes - Route configuration as flat array.
 *
 * @return {Route} - Route that matches the given route name.
 */
export const findRouteByRouteName = (nameToFind, routes) => {
  const route = findRouteByName(nameToFind, routes);
  if (!route) {
    throw new Error(`Component "${nameToFind}" was not found.`);
  }
  return route;
};

/**
 * Get the canonical URL from the given location
 *
 * @param {Array<{ route }>} routes - Route configuration as flat array
 * @param {Object} location - location object from React Router
 *
 * @return {String} Canonical URL of the given location
 *
 */
export const canonicalRoutePath = (routes, location, pathOnly = false) => {
  const { pathname, search, hash } = location;

  const matches = matchPathname(pathname, routes);
  const isListingRoute = matches.length === 1 && matches[0].route.name === 'ListingPage';

  if (isListingRoute) {
    // Remove the dynamic slug from the listing page canonical URL

    // Remove possible trailing slash
    const cleanedPathName = pathname.replace(/\/$/, '');
    const parts = cleanedPathName.split('/');

    if (parts.length !== 4) {
      throw new Error('Expected ListingPage route to have 4 parts');
    }
    const canonicalListingPathname = `/${parts[1]}/${parts[3]}`;
    return pathOnly ? canonicalListingPathname : `${canonicalListingPathname}${search}${hash}`;
  }

  return pathOnly ? pathname : `${pathname}${search}${hash}`;
};

// Parameters that only say where a visitor came from (ads, newsletters, shared
// links). They never change what a page shows.
const TRACKING_PARAM = /^(utm_\w+|fbclid|gclid|gbraid|wbraid|dclid|msclkid|ttclid|igshid|mc_cid|mc_eid|_gl|srsltid)$/;

// On the search page these make a page of its own, in this order. Sorting and
// the other filters show a category's listings in another order or a subset of
// them, so those URLs name the category page as theirs.
const SEARCH_PAGE_PARAMS = [
  'pub_categoryLevel1',
  'pub_categoryLevel2',
  'pub_categoryLevel3',
  'keywords',
];
const SEARCH_ROUTE_NAMES = ['SearchPage', 'SearchPageWithListingType'];
// The homepage's query only switches its carousel tabs
// (?strip-recentlyListed=sko): the page itself is /.
const LANDING_ROUTE_NAME = 'LandingPage';
// A partner's page is paged like search (?page=2), and nothing else in its
// query changes what it shows. The Butikker overview takes no query at all.
const PARTNER_ROUTE_NAME = 'PartnerDetailsPage';
const PARTNERS_OVERVIEW_ROUTE_NAME = 'PartnerPage';

// Page 2 and on are pages of their own; page 1 is the address without ?page.
const keepPageNumber = (params, kept) => {
  const page = Number.parseInt(params.get('page'), 10);
  if (page > 1) {
    kept.set('page', `${page}`);
  }
};

/**
 * The canonical URL path that search engines are given (`<link rel="canonical">`
 * and og:url). Same as canonicalRoutePath, without tracking parameters, and on
 * the search page only the category, a word search and the page number, in a
 * fixed order. Analytics keeps using canonicalRoutePath, which leaves the query
 * as it is.
 *
 * @param {Array<{ route }>} routes - Route configuration as flat array
 * @param {Object} location - location object from React Router
 *
 * @return {String} e.g. /s?pub_categoryLevel1=klar&pub_categoryLevel2=klar-kjoler
 */
export const seoCanonicalPath = (routes, location) => {
  const { pathname, search = '', hash = '' } = location;
  const params = new URLSearchParams(search);
  const matches = matchPathname(pathname, routes);
  const isSearchRoute = matches.length === 1 && SEARCH_ROUTE_NAMES.includes(matches[0].route.name);

  const kept = new URLSearchParams();
  if (isSearchRoute) {
    SEARCH_PAGE_PARAMS.forEach(key => {
      if (params.get(key)) {
        kept.set(key, params.get(key));
      }
    });
    keepPageNumber(params, kept);
  } else if (matches.length === 1 && matches[0].route.name === PARTNER_ROUTE_NAME) {
    keepPageNumber(params, kept);
  } else if (
    matches.length === 1 &&
    [LANDING_ROUTE_NAME, PARTNERS_OVERVIEW_ROUTE_NAME].includes(matches[0].route.name)
  ) {
    // No query kept.
  } else {
    params.forEach((value, key) => {
      if (!TRACKING_PARAM.test(key)) {
        kept.append(key, value);
      }
    });
  }
  const query = kept.toString();
  // /s/<listing type> shows the same search as /s: one listing type is in use.
  const canonicalPathname = isSearchRoute
    ? findRouteByName('SearchPage', routes)?.path || pathname
    : pathname;
  const path = canonicalRoutePath(routes, {
    pathname: canonicalPathname,
    search: query ? `?${query}` : '',
    hash,
  });
  return isSearchRoute ? path.replace(/#.*$/, '') : path;
};

// Regex that replaces {userId}, {listingId} or {userEmail} in the href string
// with URI encoded user email and ID
export const replaceParamsInHref = (href, params) => {
  return href.replace(/{(userId|userEmail|listingId)}/g, (match, key) => {
    if (params[key] != null) {
      return encodeURIComponent(params[key]);
    }
    return match;
  });
};

// This function generates data required to render ExternalLink and InternalLink components.
// It is given a URL and it replaces the {userId} and {userEmail} placeholders
// with the corresponding user data, encodes the values, and determines whether the URL
// is internal or external. If it's an internal link, the function resolves the appropriate
// route and returns an object containing route information. For external links, it returns
// the processed link without route details.
export const generateLinkProps = (type, href, routeConfiguration, userId, userEmail, listingId) => {
  const params = { userId, userEmail, listingId };
  const processedLink = replaceParamsInHref(href, params);

  const isInternalLink = type === 'internal' || href.charAt(0) === '/';

  if (isInternalLink) {
    const testURL = new URL('http://my.marketplace.com' + processedLink);

    const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
    if (matchedRoutes.length > 0) {
      const found = matchedRoutes[0];
      const to = { search: testURL.search, hash: testURL.hash };

      // Return an object with route info
      return {
        link: processedLink,
        route: {
          name: found.route?.name,
          params: found.params,
          to,
        },
      };
    }
  }
  // If not internal, return the processed external link without route info
  return {
    link: processedLink,
  };
};
